/**
 * VehicleModalHeader - Header compacto do modal premium de veículo
 *
 * Mesmo padrão visual do header do modal de cliente (48px, badges + ações).
 *
 * @version 1.6.0
 * RELEASE: 2026-10-02 v1.6.0 - Etiqueta de Status do veículo no header (VehicleStatusBadge),
 *          logo após a descrição; fallback "Ativo" quando o veículo não tem status definido.
 * RELEASE: 2026-09-29 v1.5.0 - Ícone do tipo de veículo removido do header: a identificação
 *          visual do veículo fica somente na foto da sidebar (seção Identificação).
 * RELEASE: 2026-09-29 v1.4.0 - Foto de perfil do veículo removida do header: agora vive
 *          na sidebar direita, seção Identificação, como primeira opção (VehicleModalSidebar).
 * RELEASE: 2026-09-29 v1.3.0 - Miniatura da foto de perfil do veículo (VehiclePhotoField).
 * RELEASE: 2026-09-26 v1.2.0 - Placa padronizada no formato Mercosul
 *          (VehiclePlateChip), alinhado ao modal de vistoria.
 * RELEASE: 2026-08-19 v1.1.0 - Botão "Transferir veículo" (troca de proprietário)
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial
 */

import { Pencil, X, ReceiptText, ArrowLeftRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
// v1.5.0: VehicleTypeCell removido do header (identificação visual fica na foto da sidebar)
// v1.2.0: placa padronizada no formato Mercosul compartilhado.
import { VehiclePlateChip } from '@/components/vehicles/VehiclePlateChip';
// v1.4.0: VehiclePhotoField removido do header (movido para a sidebar)
// v1.6.0: etiqueta de Status do veículo (catálogo híbrido de status)
import { VehicleStatusBadge } from '@/components/vehicles/VehicleStatus';
import type { VehicleDetailsVehicle } from '@/hooks/useVehicleDetails';

interface VehicleModalHeaderProps {
  vehicle: VehicleDetailsVehicle;
  onEdit: () => void;
  onOpenFines?: () => void;
  canViewFines?: boolean;
  /** v1.1.0: abre o modal de transferência de propriedade */
  onTransfer?: () => void;
  /** v1.1.0: permissão de editar veículos */
  canTransfer?: boolean;
  onClose: () => void;
}

/** Header do modal (48px) */
export function VehicleModalHeader({
  vehicle,
  onEdit,
  onOpenFines,
  canViewFines = false,
  onTransfer,
  canTransfer = false,
  onClose,
}: VehicleModalHeaderProps) {

  const description = [vehicle.brand_name, vehicle.model_name, vehicle.year]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="flex items-center gap-3 px-4 h-12 border-b bg-background shrink-0">
      {/* v1.4.0: foto removida do header — vive na sidebar (Identificação) */}

      {/* Placa em destaque no formato Mercosul padronizado (v1.2.0). */}
      <VehiclePlateChip plate={vehicle.plate} />

      {/* v1.5.0: ícone do tipo removido — descrição truncada assume o espaço */}

      {/* Descrição truncada */}
      <h2 className="text-sm font-semibold truncate flex-1 min-w-0">
        {description || 'Veículo sem detalhes'}
      </h2>

      {/* v1.6.0: Status do veículo (etiqueta com a cor do catálogo) */}
      <VehicleStatusBadge statusId={vehicle.status_id} className="shrink-0" />

      <div className="h-4 w-px bg-border shrink-0" />

      <div className="flex items-center gap-0.5 shrink-0">
        {canViewFines && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onOpenFines}>
                <ReceiptText className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p className="text-xs">Multas do veículo</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* v1.1.0: transferência de propriedade */}
        {canTransfer && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onTransfer}>
                <ArrowLeftRight className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p className="text-xs">Transferir veículo</p>
            </TooltipContent>
          </Tooltip>
        )}


        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p className="text-xs">Editar veículo</p>
          </TooltipContent>
        </Tooltip>

        {/* Separador antes do fechar (paridade com o modal de cliente) */}
        <div className="h-4 w-px bg-border mx-1" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-muted" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p className="text-xs">Fechar</p>
          </TooltipContent>
        </Tooltip>

      </div>
    </div>
  );
}
